import React, { useEffect, useMemo, useState } from "react"
import ZoomControls from "./MapFooterZoomControls"
import { PDFPanelWidth, rightBorderWidth, zIndex } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { selectModalIsFullScreen } from "panels/content/side-panel/slice"
import classNames from "classnames"
import { MapFooterScaleBar } from "./MapFooterScaleBar"
import { MapSearch } from "../search/MapSearch"
import { selectIsPictureInPicture, selectWeatherEnabled } from "../control/selectors"
import { WeatherControls } from "../layers/weather/controls/WeatherControls"
import { WeatherLayersFooterButton } from "./WeatherLayersFooterButton"
import { MapFooterUndoRedo } from "./MapFooterUndoRedo"
import { useMapBackground } from "../control/useMapStyles"
import { MapCompass } from "./Compass"
import { getSnapshotState } from "runlevel/actions"
import { useViewport } from "../GlobalViewState"
import { computeSnapshotFrame } from "@nova/component-map-snapshot"
import { MapTerrainButton } from "./MapTerrainButton"
import { selectColorFinderIsOpen } from "plugins/ColorFinder/V2/redux/selectors"
import { MapFooterLayersPanel } from "./MapFooterLayersPanel"

const WEATHER_PANEL_TRANSITION_MS = 300

export default function MapFooter() {
    const isFullscreenModalOpen = useAppSelector(selectModalIsFullScreen())
    const isColorFinderOpen = useAppSelector(selectColorFinderIsOpen)
    const shouldHide = isFullscreenModalOpen || isColorFinderOpen

    const isPictureInPicture = useAppSelector(selectIsPictureInPicture)
    const pdfOpen = useAppSelector(selectSnapshotOpen())
    const isSnapshot = !!getSnapshotState()
    const viewport = useViewport()

    const [weatherMenuOpen, setWeatherMenuOpen] = useState(false)

    const weather = useAppSelector(selectWeatherEnabled)

    // Drive the second-row slide here (not in WeatherControls). `weatherOpen` runs the
    // slide; `weatherMounted` defers unmounting until the close finishes, so the panel
    // animates out and the timeline RAF loop still stops on unmount (see WeatherTimelineControls).
    const [weatherMounted, setWeatherMounted] = useState(weather)
    const [weatherOpen, setWeatherOpen] = useState(weather)
    useEffect(() => {
        if (weather) {
            setWeatherMounted(true)
            const frame = requestAnimationFrame(() => setWeatherOpen(true))
            return () => cancelAnimationFrame(frame)
        }
        setWeatherOpen(false)
        const timer = setTimeout(() => setWeatherMounted(false), WEATHER_PANEL_TRANSITION_MS)
        return () => clearTimeout(timer)
    }, [weather])

    const { isLight } = useMapBackground()
    const snapshotBorderOffset = useMemo(() => {
        const { offsetY } = computeSnapshotFrame({ viewport, maxHeight: 0.8, maxWidth: 0.9 })
        return offsetY
    }, [viewport])

    return !shouldHide && !isPictureInPicture ? (
        <div
            className="absolute flex flex-col select-none pointer-events-none left-2 bottom-2"
            style={{
                zIndex: zIndex.Footer,
                right: rightBorderWidth + (pdfOpen ? PDFPanelWidth : 0),
                paddingRight: pdfOpen ? 8 : 0,
            }}
        >
            {/* First Row */}
            <div className="flex items-end w-full">
                <MapFooterUndoRedo isLight={isLight} />

                {/* Left Side */}
                <div className="flex-1 flex items-center justify-start gap-1 flex-wrap-reverse">
                    <MapFooterLayersPanel viewType="project" />

                    {/* Weather Layers */}
                    <div className="relative pointer-events-auto">
                        <WeatherLayersFooterButton
                            isLight={isLight}
                            isOpen={weatherMenuOpen}
                            onOpenChange={setWeatherMenuOpen}
                        />
                    </div>

                    <div className="relative pointer-events-auto">
                        <div className="flex items-start w-[104px]">
                            <MapTerrainButton isLight={isLight} />
                        </div>
                    </div>
                </div>

                {/* Center */}
                <div
                    className={classNames("w-96 max-w-[90vw]", {
                        "absolute left-1/2 -translate-x-1/2": isSnapshot,
                    })}
                    style={isSnapshot ? { bottom: snapshotBorderOffset } : undefined}
                >
                    <div className="flex-1 flex items-center justify-center pointer-events-auto relative h-9">
                        <MapSearch />
                    </div>
                </div>

                {/* Right Side */}
                <div className="flex-1 flex items-center justify-end gap-3">
                    {/* If you need to change the width, make sure to verify with the ruler tool in the app. */}
                    <div className="w-[150px] h-[18px] flex items-center justify-right mt-1 overflow-y-hidden">
                        <MapFooterScaleBar isLight={isLight} />
                    </div>
                    <ZoomControls isLight={isLight} />

                    <MapCompass isLight={isLight} />
                </div>
            </div>
            {/* Second Row */}
            <div
                className={classNames(
                    "grid w-full transition-all duration-300 ease-out",
                    weatherOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                )}
            >
                <div className="min-h-0 overflow-hidden pt-2">
                    {weatherMounted && <WeatherControls viewType="project" />}
                </div>
            </div>
        </div>
    ) : null
}
